import { useState } from 'react';
import {
  makeStyles,
  Typography,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import Modal from 'components/Shared/Modal';
import Step1TableSelection from './AddAccountModal/Step1TableSelection';
import Step2AccountSettings from './AddAccountModal/Step2AccountSettings';
import StepIndicator from './AddAccountModal/StepIndicator';
import { addUserAccount } from 'redux_store/slices/userAccountManagementSlice';
import { addAccountsToUser } from 'redux_store/api/endpoints/users';
import { enqueueSnackbar } from 'redux_store/slices/notificationsSlice';
import { SNACKBAR_VARIANTS } from 'redux_store/constants';

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
  },
  content: {
    padding: theme.spacing(3, 2),
    minHeight: 400,
    height: '100%',
    maxHeight: '75vh',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#c1c1c1',
      borderRadius: 4,
      '&:hover': {
        background: '#a8a8a8',
      },
    },
  },
  titleChildrenStyle: {
    width: 'auto',
    minWidth: 200,
    flexShrink: 0,
  },
  actions: {
    padding: theme.spacing(3, 4),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '0 0 16px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 10,
    padding: theme.spacing(1.5, 3),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
  },
  cancelButton: {
    color: theme.palette.text.secondary,
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  settingsStep: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: '#666',
    fontSize: 16,
  },
  stepContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

const AddAccountModal = ({ open, onClose, accountUuid, userUuid }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { en: { btn, titles } } = useSelector((state) => state.i18n);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedAccounts([]);
    setIsSaving(false);
    setValidationErrors({});
    onClose();
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedAccounts(newSelection);
  };

  const handleNextStep = () => {
    if (selectedAccounts.length > 0) {
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(1);
  };

  const handleAccountsSubmit = async (accountsWithSettings) => {
    setIsSaving(true);
    setValidationErrors({}); // Clear previous validation errors

    try {
      // Extract account IDs and settings from the form data
      const accountIds = accountsWithSettings.map(account => account.uuid || account.id);

      // Settings should be the same for all accounts (from Step2)
      const settings = accountsWithSettings[0] ? {
        role: accountsWithSettings[0].role,
        providerTitle: accountsWithSettings[0].providerTitle || '',
        providerNPINumber: accountsWithSettings[0].providerNPINumber || '',
        userPermissions: accountsWithSettings[0].userPermissions || {}
      } : {};

      // Call the API
      const result = await dispatch(addAccountsToUser({
        userUuid,
        accountIds,
        settings
      }));


      if (addAccountsToUser.fulfilled.match(result)) {
        // Success - add accounts to Redux store
        if (accountsWithSettings && accountsWithSettings.length > 0) {
          accountsWithSettings.forEach(account => {
            const accountUUID = account.uuid || account.accountUUID;
            const newAccountData = {
              ...account,
              id: accountUUID, // Set id same as accountUUID for consistency
              accountUUID: accountUUID, // Ensure accountUUID is set
              accountBusinessName: account.businessName,
              // userPermissions is already in account, no need to duplicate
            };


            dispatch(addUserAccount(newAccountData));
          });
        }

        // Show success notification
        dispatch(enqueueSnackbar({
          message: `Successfully added ${accountsWithSettings.length} account(s)`,
          options: { variant: SNACKBAR_VARIANTS.success }
        }));

        handleClose();
      } else if (addAccountsToUser.rejected.match(result)) {
        // Handle errors
        if (result.payload?.validationErrors) {
          // Show validation errors in the form
          setValidationErrors(result.payload.validationErrors);
        } else {
          // Show general error notification
          dispatch(enqueueSnackbar({
            message: result.payload?.error || 'Failed to add accounts',
            options: { variant: SNACKBAR_VARIANTS.error }
          }));
        }
      }
    } catch (error) {
      dispatch(enqueueSnackbar({
        message: 'An unexpected error occurred',
        options: { variant: SNACKBAR_VARIANTS.error }
      }));
    } finally {
      setIsSaving(false);
    }
  };



  const renderStep1 = () => (
    <Step1TableSelection
      accountUuid={accountUuid}
      selectedAccounts={selectedAccounts}
      onSelectionChange={handleSelectionChange}
      isLoading={isLoading}
    />
  );

  const renderStep2 = () => (
    <Step2AccountSettings
      selectedAccounts={selectedAccounts}
      onSubmit={handleAccountsSubmit}
      validationErrors={validationErrors}
      setValidationErrors={setValidationErrors}
    />
  );

  const getTitle = () => {
    return titles.addAccountsToUser || 'Add Accounts to User';
  };

  return (
    <Modal
      open={open}
      title={getTitle()}
      titleChildren={<StepIndicator currentStep={currentStep} />}
      titleChildrenStyle={classes.titleChildrenStyle}
      handleClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className: classes.paper
      }}
    >
      <DialogContent className={classes.content}>
        <div className={classes.stepContent}>
          {currentStep === 1 ? renderStep1() : renderStep2()}
        </div>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <div>
          {currentStep === 2 && (
            <Button
              onClick={handlePrevStep}
              className={`${classes.actionButton} ${classes.cancelButton}`}
              variant="outlined"
            >
              {btn.back || 'Back'}
            </Button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            onClick={handleClose}
            className={`${classes.actionButton} ${classes.cancelButton}`}
            variant="outlined"
          >
            {btn.cancel || 'Cancel'}
          </Button>
          {currentStep === 1 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNextStep}
              disabled={selectedAccounts.length === 0}
              className={classes.actionButton}
            >
              {btn.next || 'Next'} ({selectedAccounts.length})
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              disabled={isSaving}
              onClick={() => {
                // Trigger form submission programmatically
                document.getElementById('account-settings-form')?.dispatchEvent(
                  new Event('submit', { cancelable: true, bubbles: true })
                );
              }}
              className={classes.actionButton}
            >
              {isSaving ? 'Saving...' : (btn.addAccounts || 'Add Accounts')}
            </Button>
          )}
        </div>
      </DialogActions>
    </Modal>
  );
};

export default AddAccountModal;
